import { defaultPageIdSelector } from '@mm/state/selectors/pageSelectors';
import {
  activeChatbotIdSelector,
  connectedPageSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useActor, useSelector as useXstateSelector } from '@xstate/react';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { createContext } from 'use-context-selector';
import { Interpreter, InterpreterStatus } from 'xstate';
import { useFetchDialogues } from './hooks/use-fetch-dialogues';
import {
  createLivechatMachine,
  LcContext,
  LcEventSchema,
} from './liveChatMachine';

export type RemoteUserId = string | undefined;

type LiveChatContextType = ReturnType<
  typeof useLiveChatContext
>['liveChatContextValue'];

export const LiveChatContext = createContext({} as LiveChatContextType);

export const useLiveChatContext = (
  lcMachine: ReturnType<typeof createLivechatMachine>,
  lcService: Interpreter<
    LcContext,
    any,
    LcEventSchema,
    {
      value: any;
      context: LcContext;
    }
  >,
) => {
  const botId = useSelector(activeChatbotIdSelector);
  const remoteFbPageId = useSelector(
    (state: StoreState) => state.connectedPage.pageDetails?.remoteId || '',
  );
  const location = useLocation();

  const connectedPage = useSelector(connectedPageSelector);

  const lcServiceRef = useRef<typeof lcService | null>();

  const botLanguage = useSelector(
    (state: StoreState) =>
      (state.chatbot.activeChatbot && state.chatbot.activeChatbot.language) ||
      'English',
  );

  const [lcMachineState, send] = useActor(lcService);

  const activeBotDefaultPageId = useSelector((state: StoreState) =>
    defaultPageIdSelector(state)?.toString(),
  );

  const isLcServiceRunning = lcService.status === InterpreterStatus.Running;

  useEffect(() => {
    if (
      (!lcServiceRef.current && !!lcService) ||
      lcServiceRef.current?.sessionId !== lcService.sessionId
    ) {
      lcServiceRef.current = lcService;
    }
  }, [lcService, lcService.sessionId]);

  const remoteUserId = useXstateSelector(
    lcService,
    state => state.context.remoteUserId,
  );

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = () => {
    if (isLcServiceRunning) {
      selectedContactMachine?.send({
        type: 'LC_CONTACT_REFRESH',
        payload: remoteUserId || '',
      });
    }
  };

  useEffect(() => {
    const ref = setTimeout(refreshSelectedContact, 10);
    return () => clearTimeout(ref);
  }, [remoteUserId, selectedContactMachine?.id]);

  // Subscribing the machine to Redux and other changes
  useEffect(() => {
    if (
      isLcServiceRunning &&
      lcMachineState.context.remoteFbPageId !== remoteFbPageId
    ) {
      send({ type: 'LC_REMOTE_FB_PAGE_ID_CHANGE', payload: remoteFbPageId });
    }
  }, [isLcServiceRunning, remoteFbPageId]);

  useEffect(() => {
    if (
      isLcServiceRunning &&
      lcMachineState.context.activeChatbotId !== botId
    ) {
      send({ type: 'LC_BOT_ID_CHANGE', payload: botId }, { delay: 10 });
    }
  }, [isLcServiceRunning, botId]);

  useEffect(() => {
    if (
      isLcServiceRunning &&
      !isEqual(lcMachineState.context.connectedPage, connectedPage)
    ) {
      send({ type: 'LC_CONTEXT_UPDATE', payload: { connectedPage } });
    }
  }, [isLcServiceRunning, connectedPage]);

  useEffect(() => {
    if (
      isLcServiceRunning &&
      !isEqual(lcMachineState.context.botLanguage, botLanguage)
    ) {
      send({ type: 'LC_CONTEXT_UPDATE', payload: { botLanguage } });
    }
  }, [isLcServiceRunning, botLanguage]);

  useEffect(() => {
    if (
      isLcServiceRunning &&
      activeBotDefaultPageId !== lcMachineState.context.activeBotDefaultPageId
    ) {
      send({
        type: 'LC_BOT_DEFAULT_PAGE_ID_CHANGE',
        payload: activeBotDefaultPageId,
      });
    }
  }, [activeBotDefaultPageId]);

  useEffect(() => {
    send({ type: 'LC_LOCATION_SEARCH_CHANGE' });
  }, [isLcServiceRunning, location.search]);

  useFetchDialogues();

  const liveChatContextValue = useMemo(() => {
    return {
      lcServiceRef,
    };
  }, [lcServiceRef.current]);

  const addLiveChatContext = useCallback(
    (children: React.ReactNode) => {
      return (
        <LiveChatContext.Provider value={liveChatContextValue}>
          {children}
        </LiveChatContext.Provider>
      );
    },
    [liveChatContextValue],
  );

  return useMemo(() => {
    return {
      addLiveChatContext,
      liveChatContextValue,
    };
  }, [lcServiceRef, addLiveChatContext]);
};
